import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import ResetButtonComponent from '../components/mapbox-reset-button';
import {
  layerExists,
  safeAddSource,
  updateLayer,
  flattenMultipolygonCoords,
  getsCoordsOfAGroupOfSeats,
  getMiddleOfCoordsArray,
} from '../helper-functions/mapbox_helpers';
import {
  LAYER_SEATS_HIGHLIGHTED_ID,
  LAYER_SECTIONS_HIGHLIGHTED,
  LAYER_ROWS_HIGHLIGHTED,
  MAPBOX_TOKEN,
  RESET_CONTROL_CONTAINER_CLASSNAME,
  RESET_CONTROL_COMPONENT_CLASSNAME,
} from '../mapbox-constants/mapbox_constants';

import MapControl from './control';

class MapboxVoidSSR {}
const mapboxgl = __CLIENT__ ? require('mapbox-gl') : {Popup: MapboxVoidSSR};

function mapEventOncePromise(mapInstance, eventListener, rejectMessage, timeout = 2000) {
  return new Promise((resolve, reject) => {
    mapInstance.once(eventListener, resolve);
    setTimeout(() => reject(rejectMessage), timeout);
  });
}

function isPencilBannerFound() {
  if (__CLIENT__) {
    const elementsFound = document.getElementsByClassName('PencilBanner_content');
    if (elementsFound) {
      return true;
    }
  }
}

class Map extends React.Component {
  constructor(mapConfig) {
    super(mapConfig);
    const {defaultConfig, mapConfigData} = this.props;

    const isPencilBanner = isPencilBannerFound();
    const newDefaultConfig =
      isPencilBanner && defaultConfig.centerWithPencilBanner
        ? {...defaultConfig, ...{center: defaultConfig.centerWithPencilBanner}}
        : defaultConfig;
    const config = {...newDefaultConfig, ...mapConfig};

    this.config = config;
    this.queryRenderedFeatures.bind(this);
    this.defaultZoom = mapConfigData.ZOOM_MIN_SECTIONS;

    const ResetButton = (
      <ResetButtonComponent wdith="40px" height="40px" className={RESET_CONTROL_COMPONENT_CLASSNAME} />
    );
    // This var is used in seatMapMapboxView
    // eslint-disable-next-line react/no-unused-class-component-methods
    this.resetControl = new MapControl(this.reset, RESET_CONTROL_CONTAINER_CLASSNAME, ResetButton);
  }

  /**
   * Convenience function to collapse the interface
   * @param config
   * @returns {*|Array|{}|Array<Object>}
   */
  queryRenderedFeatures(first, second) {
    // If second param is set, it should be a config, and first should be event, else first is config
    const config = second ? second : first;

    // There may be layer or layers property set
    let {layer} = config;
    if (config.layers && config.layers.length > 0) {
      [layer] = config.layers;
    }

    // Check if layer is existing before trying to query it
    const hasLayer = layerExists(this.instance, layer);
    let result = [];
    if (hasLayer) {
      if (second) {
        result = this.instance.queryRenderedFeatures(first, config);
      } else {
        result = this.instance.queryRenderedFeatures(config);
      }
    }

    return result;
  }

  getFeatures = ({layer, filter}) => {
    const layerExisting = layerExists(this.instance, layer);
    if (layerExisting) {
      return this.queryRenderedFeatures({
        layer,
        filter,
      });
    }
    console.log(`layer ${layerId} is not loaded`); //eslint-disable-line

    return [];
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  getCurrentLayerId = (feature) => {
    if (feature && feature.layer && feature.layer.id) {
      return feature.layer.id;
    }

    return null;
  };

  // TODO: evaluate if map should know about section row seat
  // eslint-disable-next-line react/no-unused-class-component-methods
  zoomToSection = (sectionId) => {
    const [feature] = this.getFeatures({
      layer: LAYER_SECTIONS_HIGHLIGHTED,
      filter: ['==', 'sectionId', sectionId],
    });

    if (feature && feature.geometry && feature.geometry.coordinates) {
      const {geometry} = feature;
      const {coordinates} = geometry;

      if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
        const bounds = this.getBounds(feature.geometry.coordinates[0]);
        this.instance.fitBounds(bounds, {padding: 10});
      } else {
        return mapEventOncePromise(this.instance, 'moveend', 0); // Return reject promise for fail
      }

      return mapEventOncePromise(this.instance, 'moveend');
    }

    return mapEventOncePromise(this.instance, 'moveend', 0); // Return reject promise for fail
  };

  // TODO: evaluate if map should know about section row seat
  // eslint-disable-next-line react/no-unused-class-component-methods
  zoomToSeat = (uniqueSeatId) => {
    const [firstFeature] = this.getFeatures({
      layer: LAYER_SEATS_HIGHLIGHTED_ID,
      filter: ['==', 'usid', uniqueSeatId],
    });

    return this.flyToFeature(firstFeature);
  };

  // TODO: evaluate if map should know about section row seat
  // eslint-disable-next-line react/no-unused-class-component-methods
  zoomToRow = (uniqueRowId) => {
    // TODO: fix this filter
    const [firstFeature] = this.getFeatures({
      layer: LAYER_ROWS_HIGHLIGHTED,
      filter: ['==', 'ri', uniqueRowId],
    });

    return this.flyToFeature(firstFeature);
  };

  // TODO: evaluate if this is needed
  flyToHome = () => {
    // TODO: use default config to to reduce duplication
    const {mapConfigData} = this.props;
    const isPencilBanner = isPencilBannerFound();
    const pencilBannerCenter = mapConfigData.MAP_INITAL_CENTER_PENCIL_BANNER;

    this.instance.fitBounds(this.getBounds(mapConfigData.MAP_BOUND_BOX_COORDS), {
      maxZoom: mapConfigData.ZOOM_MAX_SECTIONS,
      center: isPencilBanner && pencilBannerCenter ? pencilBannerCenter : mapConfigData.MAP_INITAL_CENTER,
      duration: mapConfigData.FLYTOHOME_DURATION,
    });
    this.instance.once('moveend', () => {
      this.defaultZoom = this.instance.getZoom();
    });
  };

  mobileFlyToHome = () => {
    const {defaultConfig, mapConfigData} = this.props;
    const isPencilBanner = isPencilBannerFound();

    const center = isPencilBanner ? defaultConfig.centerWithPencilBanner : mapConfigData.MAP_INITAL_CENTER;
    this.instance.easeTo({
      center,
      zoom: this.defaultZoom,
      duration: 900,
    });
    this.instance.once('moveend', () => {
      this.instance.fitBounds(this.getBounds(mapConfigData.MAP_BOUND_BOX_COORDS), {
        maxZoom: mapConfigData.ZOOM_MAX_SECTIONS,
        center,
        duration: 500,
      });
    });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  flyToRow(feature) {
    const {mapConfigData} = this.props;
    this.instance.flyTo({
      zoom: mapConfigData.ZOOM_SELECTED_ROWS,
      center: this.rowCenter(feature),
    });
  }

  rowCenter(feature) {
    if (feature && feature.geometry && feature.geometry.coordinates) {
      const center = this.getBounds(feature.geometry.coordinates).getCenter();

      return center;
    }

    return null;
  }

  polygonSectionCenter(feature) {
    if (feature) {
      const coords = get(feature, 'geometry.coordinates[0]', null);

      return this.getBounds(coords).getCenter();
    }

    return null;
  }

  multipolygonSectionCenter(feature) {
    if (feature) {
      const coords = get(feature, 'geometry.coordinates', null);
      const flattenedCoords = coords ? flattenMultipolygonCoords(coords, []) : null;

      return this.getBounds(flattenedCoords).getCenter();
    }

    return null;
  }

  multiLineStringRowCenter = (feature) => {
    if (feature) {
      const coords = get(feature, 'geometry.coordinates', null);
      const flattenedCoords = coords ? flattenMultipolygonCoords(coords, []) : null;

      return this.getBounds(flattenedCoords).getCenter();
    }

    return null;
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  resetMap = () => {
    this.flyToHome();
    // TODO: reset all config to default
    this.reset();
  };

  reset = () => {
    const {device, updateQuery, setListingSelected, clickTooltip, removeResetControl} = this.props;

    const updateQueryObj = {
      sectionIdList: false,
      listingRows: false,
      zoneIdList: false,
      byoQuantity: false,
    };

    clickTooltip.remove();
    if (device === 'desktop') {
      updateQueryObj.showBYO = false;
      updateQueryObj.listingId = false;

      updateQuery(updateQueryObj, true);
      this.flyToHome();
    } else {
      updateQuery(updateQueryObj, true);

      this.mobileFlyToHome();
      setListingSelected(null);
    }
    removeResetControl();
  };

  getBounds(coordinates) {
    // TODO: evaluate this code for optimization
    if (coordinates) {
      return coordinates.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
    }

    return null;
  }

  /**
   * Map fly to mapbox feature
   * @param feature
   */
  flyToFeature(feature, fitBoundsZoomLevel) {
    /*
     * TODO: coordinates returned by mapbox of the same polygon vary if we zoom in to a certain Level,
     *       so the center based on bounds also varies, which actually gives better experience........
     *       need to discuss in the future.
     */

    if (feature && feature.geometry && feature.geometry.coordinates) {
      const {mapConfigData} = this.props;
      const {geometry} = feature;
      const {coordinates} = geometry;

      if (!coordinates || !Array.isArray(coordinates)) {
        return mapEventOncePromise(this.instance, 'moveend', 0);
      }

      if (feature.geometry.type === 'Polygon') {
        const bounds = this.getBounds(feature.geometry.coordinates[0]);
        this.instance.fitBounds(bounds, {
          zoom: fitBoundsZoomLevel,
          speed: 1.5,
        });
      } else if (feature.geometry.type === 'LineString') {
        this.instance.flyTo({
          zoom: mapConfigData.ZOOM_SELECTED_ROWS,
          center: this.rowCenter(feature),
        });
      } else if (feature.geometry.type === 'MultiLineString') {
        this.instance.flyTo({
          zoom: mapConfigData.ZOOM_SELECTED_ROWS,
          center: this.multiLineStringRowCenter(feature),
        });
      } else if (feature.geometry.type === 'MultiPolygon') {
        const flattenedCoords = flattenMultipolygonCoords(coordinates, []);
        this.instance.fitBounds(this.getBounds(flattenedCoords), {
          zoom: fitBoundsZoomLevel,
          speed: 1.5,
        });
      } else if (feature.geometry.type === 'Point') {
        this.instance.flyTo({
          center: coordinates,
          zoom: mapConfigData.ZOOM_MAX_SEATS,
        });
      } else {
        return mapEventOncePromise(this.instance, 'moveend', 0); // Return reject promise for fail
      }
    }

    return mapEventOncePromise(this.instance, 'moveend', 0); // Return reject promise for fail
  }

  /**
   * Add new layer source via layer source config
   * @param sources
   */
  addSources = (sources) => {
    for (let sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
      safeAddSource(this.instance, sources[sourceIndex]);
    }
  };

  /**
   * Add a new layer via layer class
   * @param layers
   */
  addLayers = (layers) => {
    this.updateLayers(layers);
  };

  /**
   * Used to update specific layers as needed for re-rendering
   * @param layers
   */
  updateLayers = (layers) => {
    if (layers) {
      for (let layerIndex = 0; layerIndex < layers.length; layerIndex++) {
        const {config: layerConfig, beforeLayerId} = layers[layerIndex];
        updateLayer(this.instance, layerConfig, beforeLayerId);
      }
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  centerAtAPoint = (coords, device) => {
    const {mapConfigData} = this.props;
    if (coords && Array.isArray(coords)) {
      if (device === 'desktop') {
        this.instance.flyTo({
          center: coords,
          zoom: mapConfigData.ZOOM_SELECTED_SEATS,
          offset: mapConfigData.OFFSET_SEAT_CENTER_POSITION,
          screenSpeed: 2.1,
          curve: 1.55,
        });
      } else {
        this.instance.easeTo({
          center: coords,
          offset: mapConfigData.OFFSET_SEAT_CENTER_POSITION,
          zoom: mapConfigData.ZOOM_SELECTED_SEATS,
          animate: true,
        });
      }
    }
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  centerOnFeature = (feature, seatCoords, uniqueSeatIdArray) => {
    if (feature && feature.geometry && feature.geometry.type) {
      const {mapConfigData} = this.props;
      if (feature.geometry.type === 'LineString') {
        this.instance.easeTo({
          center: this.rowCenter(feature),
          zoom: mapConfigData.ZOOM_SELECTED_ROWS,
        });
      } else if (feature.geometry.type === 'Point') {
        // TODO need to check contidtion
        if (feature.geometry.coordinates) {
          const coordsOfAGroupOfSeats = getsCoordsOfAGroupOfSeats(seatCoords, uniqueSeatIdArray);
          const center =
            coordsOfAGroupOfSeats.length !== 0
              ? getMiddleOfCoordsArray(coordsOfAGroupOfSeats)
              : feature.geometry.coordinates;

          this.instance.easeTo({
            center,
            offset: mapConfigData.OFFSET_SEAT_CENTER_POSITION,
            zoom: mapConfigData.ZOOM_SELECTED_SEATS,
            animate: true,
          });
        }
      } else if (feature.geometry.type === 'Polygon') {
        this.instance.easeTo({
          center: this.polygonSectionCenter(feature),
        });
      } else if (feature.geometry.type === 'MultiPolygon') {
        this.instance.easeTo({
          center: this.multipolygonSectionCenter(feature),
        });
      } else if (feature.geometry.type === 'MultiLineString') {
        this.instance.easeTo({
          center: this.multiLineStringRowCenter(feature),
          zoom: mapConfigData.ZOOM_SELECTED_ROWS,
        });
      }
    }
  };

  getCurrentCenterCoords = () => {
    const currentCenter = this.instance.getCenter();
    const currentCenterCoordX = currentCenter.lng;
    const currentCenterCoordY = currentCenter.lat;

    return {
      currentCenterCoordX,
      currentCenterCoordY,
    };
  };

  isMapCenterChanged = () => {
    const {defaultConfig} = this.props;
    const isPencilBanner = isPencilBannerFound();
    const center =
      isPencilBanner && defaultConfig.centerWithPencilBanner
        ? defaultConfig.centerWithPencilBanner
        : defaultConfig.center;

    let {currentCenterCoordX, currentCenterCoordY} = this.getCurrentCenterCoords();
    currentCenterCoordX = Number(Number.parseFloat(currentCenterCoordX).toFixed(3));
    currentCenterCoordY = Number(Number.parseFloat(currentCenterCoordY).toFixed(3));

    return !(center[0] === currentCenterCoordX && center[1] === currentCenterCoordY);
  };

  isZoomLevelChanged = () => {
    return !(this.defaultZoom === this.instance.getZoom());
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  isMapMoved = () => {
    return this.isMapCenterChanged() || this.isZoomLevelChanged();
  };

  mapInit = () => {
    const {sources, layers} = this.props;
    this.addSources(sources);
    this.addLayers(layers);
    this.instance.off('load', this.mapInit);
  };

  componentDidMount() {
    const {mapInstanceRef, mapInstanceWrapperRef, mapActions, onClick, wrapperMapActions} = this.props;

    mapboxgl.accessToken = MAPBOX_TOKEN;
    this.instance = new mapboxgl.Map(this.config);
    this.instance.on('load', this.mapInit);
    mapInstanceRef(this.instance);
    mapInstanceWrapperRef(this.instance);
    mapActions(this); // TODO: restrict interface to only allowed
    wrapperMapActions(this); // TODO: restrict interface to only allowed
    if (onClick) {
      this.instance.on('click', onClick);
    }
  }

  render() {
    const {container} = this.props;

    return <div id={container} />;
  }
}

Map.propTypes = {
  clickTooltip: PropTypes.shape({
    remove: PropTypes.func,
  }),
  container: PropTypes.string,
  defaultConfig: PropTypes.shape({
    center: PropTypes.any,
    centerWithPencilBanner: PropTypes.any,
  }),
  device: PropTypes.string,
  layers: PropTypes.any,
  mapActions: PropTypes.func,
  mapConfigData: PropTypes.shape({
    FLYTOHOME_DURATION: PropTypes.any,
    MAP_BOUND_BOX_COORDS: PropTypes.any,
    MAP_INITAL_CENTER: PropTypes.any,
    MAP_INITAL_CENTER_PENCIL_BANNER: PropTypes.any,
    OFFSET_SEAT_CENTER_POSITION: PropTypes.any,
    ZOOM_MAX_SEATS: PropTypes.any,
    ZOOM_MAX_SECTIONS: PropTypes.any,
    ZOOM_MIN_SECTIONS: PropTypes.any,
    ZOOM_SELECTED_ROWS: PropTypes.any,
    ZOOM_SELECTED_SEATS: PropTypes.any,
  }),
  mapInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  mapInstanceWrapperRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onClick: PropTypes.func,
  removeResetControl: PropTypes.func,
  setListingSelected: PropTypes.func,
  sources: PropTypes.array,
  updateQuery: PropTypes.func,
  wrapperMapActions: PropTypes.func,
};

export default Map;
